import { getIdToken } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { getAttribution } from '@/lib/attribution';
import { FIXTURES, fixtureFetch } from '@/lib/fixtures';

// Local dev: point at `wrangler dev` with VITE_API_URL=http://127.0.0.1:8787.
// Unset in production builds → the deployed API.
const API_URL = import.meta.env.VITE_API_URL || 'https://api.usekora.app';

export interface AuthVerifyResponse {
  success: boolean;
  user: {
    id: number;
    firebase_uid: string;
    email: string;
    display_name: string | null;
    profile_picture: string | null;
  };
  workspace_exists: boolean;
  workspace: {
    id: number;
    workspace_id: string;
    name: string;
    role: string;
    logo_url: string | null;
    ticket_workflow: string;
  } | null;
}

export interface WorkspaceByUserResponse {
  success: boolean;
  workspace_exists: boolean;
  workspace: {
    id: number;
    workspace_id: string;
    name: string;
    logo_url: string | null;
    website: string | null;
    role: string;
  } | null;
}

export interface CreateWorkspaceResponse {
  success: boolean;
  workspace: {
    id: number;
    workspace_id: string;
    name: string;
    logo_url: string | null;
    secret_key: string;
  };
}

// Fired after any successful write so the credit balance in the sidebar can refresh.
export const CREDITS_MAYBE_CHANGED_EVENT = 'kora:credits-maybe-changed';

/** An API error that keeps the server's machine-readable code (e.g. MEMBER_LIMIT_REACHED). */
export class ApiCodeError extends Error {
  code?: string;
  limit?: number;
  constructor(message: string, code?: string, limit?: number) {
    super(message);
    this.code = code;
    this.limit = limit;
  }
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const res = await apiFetchInner(path, options);
  const method = (options.method || 'GET').toUpperCase();
  if (res.ok && method !== 'GET' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CREDITS_MAYBE_CHANGED_EVENT));
  }
  return res;
}

async function apiFetchInner(path: string, options: RequestInit = {}): Promise<Response> {
  if (FIXTURES) return fixtureFetch(path, options);
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  // Firebase caches the ID token and refreshes it shortly before expiry, so a
  // forced refresh (an extra round trip to Google on EVERY call) is only done
  // as a one-time retry when the API rejects the token.
  const send = async (forceRefresh: boolean): Promise<Response> => {
    const idToken = await getIdToken(user, forceRefresh);
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
      'Authorization': `Bearer ${idToken}`,
    };
    // Don't set Content-Type for FormData — browser sets boundary automatically
    if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(`${API_URL}${path}`, { ...options, headers });
  };
  const res = await send(false);
  if (res.status === 401) return send(true);
  return res;
}

export async function authVerify(idToken: string, firebase_uid: string, email: string, display_name?: string, profile_picture?: string): Promise<AuthVerifyResponse> {
  const res = await fetch(`${API_URL}/auth/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ firebase_uid, email, display_name, profile_picture, attribution: getAttribution() }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Auth verification failed');
  return data;
}

export async function getWorkspaceByUser(firebase_uid: string): Promise<WorkspaceByUserResponse> {
  const res = await apiFetch(`/workspace/by-user/${firebase_uid}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch workspace');
  return data;
}

export async function createWorkspace(params: {
  firebase_uid: string;
  workspace_name: string;
  website?: string;
  team_size: string;
  heard_about_us: string;
  logo?: File | null;
  ticket_workflow?: string;
  categories?: string[];
  ai_auto_category?: boolean;
}): Promise<CreateWorkspaceResponse> {
  const { firebase_uid, workspace_name, website, team_size, heard_about_us, logo, ticket_workflow, categories, ai_auto_category } = params;

  const appendCategoryFields = (target: FormData | Record<string, unknown>) => {
    if (categories?.length) {
      const list = categories.map((c) => c.trim()).filter(Boolean).slice(0, 20);
      if ('append' in target) (target as FormData).append('categories', JSON.stringify(list));
      else (target as Record<string, unknown>).categories = list;
    }
    if (ai_auto_category !== undefined) {
      if ('append' in target) (target as FormData).append('ai_auto_category', String(ai_auto_category));
      else (target as Record<string, unknown>).ai_auto_category = ai_auto_category;
    }
  };

  if (logo) {
    const formData = new FormData();
    formData.append('firebase_uid', firebase_uid);
    formData.append('workspace_name', workspace_name);
    if (website) formData.append('website', website);
    formData.append('team_size', team_size);
    formData.append('heard_about_us', heard_about_us);
    if (ticket_workflow) formData.append('ticket_workflow', ticket_workflow);
    formData.append('logo', logo);
    appendCategoryFields(formData);
    const attr = getAttribution();
    if (attr) formData.append('attribution', JSON.stringify(attr));

    const res = await apiFetch('/workspace/create', {
      method: 'POST',
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create workspace');
    return data;
  }

  const payload: Record<string, unknown> = { firebase_uid, workspace_name, website, team_size, heard_about_us, attribution: getAttribution() };
  if (ticket_workflow) payload.ticket_workflow = ticket_workflow;
  appendCategoryFields(payload);
  const res = await apiFetch('/workspace/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create workspace');
  return data;
}

export interface CreateTicketResponse {
  success: boolean;
  ticket: {
    id: number;
    ticket_number: number;
    title: string;
    description: string;
    category: string;
    priority: string;
    status: string;
    reporter: string;
    assignee: string | null;
    created_at: string;
  };
}

export async function createTicket(params: {
  workspace_id: string;
  firebase_uid: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  attachments?: number[];
}): Promise<CreateTicketResponse> {
  const res = await apiFetch('/tickets/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create ticket');
  return data;
}

export interface StructureTicketResponse {
  success: boolean;
  structured: {
    title: string;
    description: string;
    category: string;
  };
}

export async function structureTicket(params: {
  raw_text: string;
  priority: string;
  firebase_uid: string;
  workspace_id: string;
}): Promise<StructureTicketResponse> {
  const res = await apiFetch('/tickets/structure', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'AI structuring failed');
  return data;
}

export interface AttachmentResponse {
  success: boolean;
  attachment: {
    id: number;
    file_name: string;
    file_type: string;
    file_size: number;
    storage_url: string;
    attachment_type: string;
    uploaded_at: string;
  };
}

export async function uploadAttachment(params: {
  file: File;
  firebase_uid: string;
  workspace_id: string;
  attachment_type?: string;
}): Promise<AttachmentResponse> {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('firebase_uid', params.firebase_uid);
  formData.append('workspace_id', params.workspace_id);
  formData.append('attachment_type', params.attachment_type || 'ticket');

  const res = await apiFetch('/attachments/upload', {
    method: 'POST',
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Upload failed (HTTP ${res.status})`);
  return data;
}

export interface SnippetResponse {
  success: boolean;
  snippets: {
    id: number;
    trigger: string;
    content: string;
    created_by: string;
    created_at: string;
  }[];
}

export async function listSnippets(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<SnippetResponse> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/snippets?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch snippets');
  return data;
}

export async function createSnippet(params: {
  firebase_uid: string;
  workspace_id: string;
  trigger: string;
  content: string;
}): Promise<{ success: boolean; snippet: { id: number; trigger: string; content: string } }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/snippets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      trigger: params.trigger,
      content: params.content,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create snippet');
  return data;
}

export async function deleteSnippet(params: {
  firebase_uid: string;
  workspace_id: string;
  snippet_id: number;
}): Promise<{ success: boolean; message: string }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/snippets/${params.snippet_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
    { method: 'DELETE' },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete snippet');
  return data;
}

export async function updateSnippet(params: {
  firebase_uid: string;
  workspace_id: string;
  snippet_id: number;
  trigger?: string;
  content?: string;
}): Promise<{ success: boolean; snippet: { id: number; trigger: string; content: string } }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/snippets/${params.snippet_id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      trigger: params.trigger,
      content: params.content,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update snippet');
  return data;
}

// ─── Ticket list / detail / update ───────────────────────

export interface TicketFilters {
  status?: string;
  category?: string;
  priority?: string;
  assignee?: string;
  raised_by?: string;
  created_from?: string;
  created_to?: string;
  sort_by?: string;
  sla_state?: string;
  /** 'portal' = only tickets raised by customer-portal customers */
  reporter_type?: 'portal';
  search?: string;
  page?: number;
  limit?: number;
}

interface RealTicketListItem {
  id: number;
  ticket_number: number;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  reporter: string;
  assignee: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  hours_open: number;
}

interface PseudoUser {
  id: number;
  firebase_uid: string;
  email: string;
  display_name: string | null;
  profile_picture: null;
}

function makePseudoUser(name: string, id = 0): PseudoUser {
  const isEmail = name.includes('@');
  return {
    id,
    firebase_uid: '',
    email: isEmail ? name : '',
    display_name: isEmail ? null : name,
    profile_picture: null,
  };
}

export async function listTickets(params: {
  firebase_uid: string;
  workspace_id: string;
  filters?: TicketFilters;
}): Promise<{
  tickets: any[];
  pagination: { total: number; page: number; limit: number; total_pages: number };
  categories?: string[];
}> {
  const searchParams = new URLSearchParams({
    firebase_uid: params.firebase_uid,
    workspace_id: params.workspace_id,
  });
  const f = params.filters || {};
  if (f.status) searchParams.set('status', f.status);
  if (f.category) searchParams.set('category', f.category);
  if (f.priority) searchParams.set('priority', f.priority);
  if (f.assignee) searchParams.set('assignee', f.assignee);
  if (f.raised_by) searchParams.set('raised_by', f.raised_by);
  if (f.created_from) searchParams.set('created_from', f.created_from);
  if (f.created_to) searchParams.set('created_to', f.created_to);
  if (f.sort_by) searchParams.set('sort_by', f.sort_by);
  if (f.sla_state) searchParams.set('sla_state', f.sla_state);
  if (f.reporter_type) searchParams.set('reporter_type', f.reporter_type);
  if (f.search) searchParams.set('search', f.search);
  if (f.page) searchParams.set('page', String(f.page));
  if (f.limit) searchParams.set('limit', String(f.limit));

  const res = await apiFetch(`/tickets?${searchParams.toString()}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch tickets');

  const tickets = (data.tickets || []).map((t: RealTicketListItem) => ({
    ...t,
    reporter_id: 0,
    assignee_id: null as number | null,
    claimed_by: null,
    claimed_at: null,
    reopened_at: null,
    closed_at: null,
    workspace_id: 0,
    reporter: makePseudoUser(t.reporter),
    assignee: t.assignee ? makePseudoUser(t.assignee) : null,
    attachments: [],
    comments: [],
    reactions: [],
    activity_log: [],
  }));

  return {
    tickets,
    pagination: data.pagination || { total: 0, page: 1, limit: 20, total_pages: 0 },
    categories: data.categories || [],
  };
}

export async function getTicket(params: {
  firebase_uid: string;
  workspace_id: string;
  ticket_id: number;
}): Promise<{ success: boolean; ticket: any }> {
  const res = await apiFetch(
    `/tickets/${params.ticket_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}&workspace_id=${encodeURIComponent(params.workspace_id)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch ticket');

  const t = data.ticket;
  const ticket = {
    ...t,
    reporter_id: 0,
    assignee_id: null as number | null,
    claimed_by: null,
    workspace_id: 0,
    reporter: t.reporter ? {
      id: t.reporter.id,
      firebase_uid: '',
      email: t.reporter.email || '',
      display_name: t.reporter.name || null,
      profile_picture: null,
      is_portal_customer: !!t.reporter.is_portal_customer,
    } : null,
    assignee: t.assignee ? {
      id: t.assignee.id,
      firebase_uid: '',
      email: t.assignee.email || '',
      display_name: t.assignee.name || null,
      profile_picture: null,
    } : null,
    attachments: (t.attachments || []).map((a: any) => ({
      id: a.id,
      file_name: a.file_name,
      file_type: a.file_type,
      file_size: a.file_size,
      storage_url: a.storage_url,
      uploaded_at: a.uploaded_at,
    })),
    comments: (t.comments || []).map((c: any) => ({
      id: c.id,
      ticket_id: params.ticket_id,
      user_id: 0,
      content: c.content,
      parent_id: c.parent_id,
      is_internal: !!c.is_internal,
      authored_by_ai: !!c.authored_by_ai,
      attachments: c.attachments || [],
      created_at: c.created_at,
      user: {
        id: c.user?.id || 0,
        firebase_uid: '',
        email: c.user?.email || '',
        display_name: c.user?.name || null,
        profile_picture: null,
      },
    })),
    reactions: (t.reactions || []).map((r: any) => ({
      id: r.id,
      ticket_id: params.ticket_id,
      comment_id: r.comment_id,
      user_id: 0,
      reaction: r.reaction,
      created_at: r.created_at,
      user: {
        id: 0,
        firebase_uid: '',
        email: '',
        display_name: r.user || null,
        profile_picture: null,
      },
    })),
    activity_log: (t.activity_log || []).map((a: any) => ({
      id: a.id,
      workspace_id: 0,
      ticket_id: params.ticket_id,
      user_id: 0,
      action: a.action,
      details: a.details,
      created_at: a.created_at,
      user: {
        id: 0,
        firebase_uid: '',
        email: '',
        display_name: typeof a.user === 'string' ? a.user : null,
        profile_picture: null,
      },
    })),
  };

  return { success: true, ticket };
}

export async function updateTicket(params: {
  firebase_uid: string;
  workspace_id: string;
  ticket_id: number;
  status?: string;
  assignee_id?: number | null;
  priority?: string;
  title?: string;
  description?: string;
  category?: string;
}): Promise<{ success: boolean }> {
  const res = await apiFetch(`/tickets/${params.ticket_id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      workspace_id: params.workspace_id,
      status: params.status,
      assignee_id: params.assignee_id,
      priority: params.priority,
      title: params.title,
      description: params.description,
      category: params.category,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update ticket');
  return data;
}

export async function claimTicket(params: {
  firebase_uid: string;
  workspace_id: string;
  ticket_id: number;
}): Promise<{ success: boolean }> {
  const res = await apiFetch(`/tickets/${params.ticket_id}/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      workspace_id: params.workspace_id,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to claim ticket');
  return data;
}

export async function addComment(params: {
  firebase_uid: string;
  workspace_id: string;
  ticket_id: number;
  content: string;
  parent_id?: number | null;
  attachments?: number[];
  /** Agent-only note: never shown in the customer portal or emailed to the reporter. */
  is_internal?: boolean;
}): Promise<{ success: boolean; comment: any }> {
  const res = await apiFetch(`/tickets/${params.ticket_id}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      workspace_id: params.workspace_id,
      content: params.content,
      parent_id: params.parent_id,
      attachments: params.attachments,
      is_internal: params.is_internal === true,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to add comment');
  return data;
}

export async function addReaction(params: {
  firebase_uid: string;
  workspace_id: string;
  ticket_id: number;
  reaction: string;
  comment_id?: number;
}): Promise<{ success: boolean }> {
  const res = await apiFetch(`/tickets/${params.ticket_id}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      workspace_id: params.workspace_id,
      reaction: params.reaction,
      comment_id: params.comment_id,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to add reaction');
  return data;
}

export async function exportTickets(params: {
  firebase_uid: string;
  workspace_id: string;
  filters?: TicketFilters;
}): Promise<void> {
  const searchParams = new URLSearchParams({
    firebase_uid: params.firebase_uid,
    workspace_id: params.workspace_id,
  });
  const f = params.filters || {};
  if (f.status) searchParams.set('status', f.status);
  if (f.created_from) searchParams.set('created_from', f.created_from);
  if (f.created_to) searchParams.set('created_to', f.created_to);

  const res = await apiFetch(`/tickets/export?${searchParams.toString()}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to export tickets');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const disposition = res.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="(.+?)"/);
  a.download = filenameMatch?.[1] || `tickets_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Members ─────────────────────────────────────────────

export async function listMembers(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; members: any[] }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/members?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch members');

  const members = (data.members || []).map((m: any) => ({
    id: m.membership_id ?? m.id,
    user_id: m.id,
    workspace_id: 0,
    role: m.role,
    joined_at: m.joined_at,
    last_active_at: m.last_active_at,
    status: m.status === 'invited' ? 'pending' : m.status,
    user: {
      id: m.id,
      firebase_uid: '',
      email: m.email || '',
      display_name: m.display_name || null,
      profile_picture: m.profile_picture || null,
    },
  }));

  return { success: true, members };
}

// ─── Workspace Settings ──────────────────────────────────

export async function getOnboardingStatus(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; steps: Record<string, boolean> }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/onboarding-status?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch onboarding status');
  return data;
}

export async function getWorkspaceSettings(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; settings: any }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/settings?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch workspace settings');
  return data;
}

export async function updateWorkspace(params: {
  firebase_uid: string;
  workspace_id: string;
  name?: string;
  website?: string;
  team_size?: string;
  logo_url?: string | null;
  ticket_workflow?: string;
  auto_close_mode?: string;
  auto_close_delay_days?: number;
}): Promise<{ success: boolean; workspace: any }> {
  const body: Record<string, any> = {
    firebase_uid: params.firebase_uid,
    name: params.name,
    website: params.website,
    team_size: params.team_size,
  };
  if (params.logo_url !== undefined) {
    body.logo_url = params.logo_url;
  }
  if (params.ticket_workflow !== undefined) {
    body.ticket_workflow = params.ticket_workflow;
  }
  if (params.auto_close_mode !== undefined) {
    body.auto_close_mode = params.auto_close_mode;
  }
  if (params.auto_close_delay_days !== undefined) {
    body.auto_close_delay_days = params.auto_close_delay_days;
  }
  const res = await apiFetch(`/workspace/${params.workspace_id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update workspace');
  return data;
}

export async function regenerateSecretKey(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; secret_key: string }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/regenerate-key?firebase_uid=${params.firebase_uid}`, {
    method: 'POST',
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to regenerate key');
  return data;
}

// ─── Team Management ──────────────────────────────────────

export async function inviteMember(params: {
  firebase_uid: string;
  workspace_id: string;
  email: string;
  role: string;
}): Promise<{ success: boolean; invitation: any }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      email: params.email,
      role: params.role,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new ApiCodeError(data.error || 'Failed to invite member', data.code, data.limit);
  return data;
}

export async function updateMemberRole(params: {
  firebase_uid: string;
  workspace_id: string;
  user_id: number;
  role: string;
}): Promise<{ success: boolean; member: any }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/members/${params.user_id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      role: params.role,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update member role');
  return data;
}

export async function removeMember(params: {
  firebase_uid: string;
  workspace_id: string;
  member_id: number;
}): Promise<{ success: boolean; message: string }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/members/${params.member_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
    { method: 'DELETE' },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to remove member');
  return data;
}

export async function resendInvitation(params: {
  firebase_uid: string;
  workspace_id: string;
  email: string;
}): Promise<{ success: boolean; invitation: any }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/invitations/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      email: params.email,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new ApiCodeError(data.error || 'Failed to resend invitation', data.code, data.limit);
  return data;
}

// ─── Dashboard Metrics ────────────────────────────────────

export async function getDashboardMetrics(params: {
  firebase_uid: string;
  workspace_id: string;
  month?: string;
}): Promise<any> {
  const searchParams = new URLSearchParams({
    firebase_uid: params.firebase_uid,
    workspace_id: params.workspace_id,
  });
  if (params.month) searchParams.set('month', params.month);

  const res = await apiFetch(`/dashboard/metrics?${searchParams.toString()}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch dashboard metrics');
  return data;
}

// ─── AI Analyst ───────────────────────────────────────────

export async function getAiHistory(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; messages: any[]; quota: any }> {
  const res = await apiFetch(
    `/dashboard/ai-analyst/history?firebase_uid=${encodeURIComponent(params.firebase_uid)}&workspace_id=${encodeURIComponent(params.workspace_id)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch AI history');
  return data;
}

export async function askAiAnalyst(params: {
  firebase_uid: string;
  workspace_id: string;
  question: string;
}): Promise<any> {
  const res = await apiFetch('/dashboard/ai-analyst', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspace_id: params.workspace_id,
      firebase_uid: params.firebase_uid,
      question: params.question,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'AI Analyst request failed');
  return data;
}

export async function clearAiHistory(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; deleted_count: number }> {
  const res = await apiFetch('/dashboard/ai-analyst/clear', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspace_id: params.workspace_id,
      firebase_uid: params.firebase_uid,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to clear AI history');
  return data;
}

// ─── Files ────────────────────────────────────────────────

export interface FileItem {
  id: number;
  source: 'ticket' | 'comment';
  file_name: string;
  file_type: string;
  file_size: number;
  storage_url: string;
  uploaded_at: string;
  ticket_id: number | null;
  ticket_number: number | null;
  ticket_title: string | null;
  uploader: {
    name: string | null;
    email: string;
    avatar: string | null;
  };
}

export async function listFiles(params: {
  firebase_uid: string;
  workspace_id: string;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; files: FileItem[]; pagination: { total: number; page: number; limit: number; total_pages: number } }> {
  const searchParams = new URLSearchParams({
    firebase_uid: params.firebase_uid,
    workspace_id: params.workspace_id,
  });
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));

  const res = await apiFetch(`/workspace/${params.workspace_id}/files?${searchParams.toString()}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch files');
  return data;
}

// ─── Webhooks ─────────────────────────────────────────────

export interface Webhook {
  id: number;
  url: string;
  description: string | null;
  events: string;
  is_active: boolean;
  has_secret: boolean;
  created_at: string;
  updated_at: string;
}

export interface WebhookDelivery {
  id: number;
  event: string;
  status: string;
  response_code: number | null;
  response_body: string | null;
  created_at: string;
}

export const WEBHOOK_EVENTS = [
  'ticket.created',
  'ticket.updated',
  'ticket.resolved',
  'ticket.closed',
  'ticket.reopened',
  'ticket.assigned',
  'ticket.claimed',
  'ticket.sla_breach',
  'article.published',
  'article.updated',
] as const;

export async function listWebhooks(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; webhooks: Webhook[]; count: number; max_allowed: number }> {
  const searchParams = new URLSearchParams({
    firebase_uid: params.firebase_uid,
    workspace_id: params.workspace_id,
  });
  const res = await apiFetch(`/webhooks?${searchParams.toString()}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch webhooks');
  return data;
}

export async function createWebhook(params: {
  firebase_uid: string;
  workspace_id: string;
  url: string;
  description?: string;
  events?: string[];
  secret_token?: string;
}): Promise<{ success: boolean; webhook: Webhook }> {
  const res = await apiFetch('/webhooks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create webhook');
  return data;
}

export async function updateWebhook(params: {
  firebase_uid: string;
  workspace_id: string;
  webhook_id: number;
  url?: string;
  description?: string;
  events?: string[];
  is_active?: boolean;
  secret_token?: string;
}): Promise<{ success: boolean }> {
  const res = await apiFetch(`/webhooks/${params.webhook_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      workspace_id: params.workspace_id,
      url: params.url,
      description: params.description,
      events: params.events,
      is_active: params.is_active,
      secret_token: params.secret_token,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update webhook');
  return data;
}

export async function deleteWebhook(params: {
  firebase_uid: string;
  workspace_id: string;
  webhook_id: number;
}): Promise<{ success: boolean }> {
  const searchParams = new URLSearchParams({
    firebase_uid: params.firebase_uid,
    workspace_id: params.workspace_id,
  });
  const res = await apiFetch(`/webhooks/${params.webhook_id}?${searchParams.toString()}`, {
    method: 'DELETE',
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete webhook');
  return data;
}

export async function testWebhook(params: {
  firebase_uid: string;
  workspace_id: string;
  webhook_id: number;
}): Promise<{ success: boolean; delivery: any }> {
  const res = await apiFetch(`/webhooks/${params.webhook_id}/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      workspace_id: params.workspace_id,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to test webhook');
  return data;
}

export async function listWebhookDeliveries(params: {
  firebase_uid: string;
  workspace_id: string;
  webhook_id: number;
}): Promise<{ success: boolean; deliveries: WebhookDelivery[] }> {
  const searchParams = new URLSearchParams({
    firebase_uid: params.firebase_uid,
    workspace_id: params.workspace_id,
  });
  const res = await apiFetch(`/webhooks/${params.webhook_id}/deliveries?${searchParams.toString()}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch deliveries');
  return data;
}

// ─── Merge & Bulk ───

export async function mergeTicket(params: {
  firebase_uid: string;
  workspace_id: string;
  ticket_id: number;
  target_ticket_id: number;
}): Promise<{ success: boolean; source_ticket: { id: number; ticket_number: number }; target_ticket: { id: number; ticket_number: number } }> {
  const res = await apiFetch(`/tickets/${params.ticket_id}/merge?firebase_uid=${encodeURIComponent(params.firebase_uid)}&workspace_id=${encodeURIComponent(params.workspace_id)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      target_ticket_id: params.target_ticket_id,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to merge ticket');
  return data;
}

export async function bulkUpdateTickets(params: {
  firebase_uid: string;
  workspace_id: string;
  ticket_ids: number[];
  status?: string;
  assignee_id?: number | null;
}): Promise<{ success: boolean; updated: Array<{ ticket_id: number; success: boolean }>; errors: Array<{ ticket_id: number; error: string }>; count: number }> {
  const res = await apiFetch(`/tickets/bulk?firebase_uid=${encodeURIComponent(params.firebase_uid)}&workspace_id=${encodeURIComponent(params.workspace_id)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ticket_ids: params.ticket_ids,
      status: params.status,
      assignee_id: params.assignee_id,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to bulk update tickets');
  return data;
}

// ─── Credits & Billing (PAYG) ────────────────────────────

export interface CreditLedgerEntry {
  delta: number;
  reason: string;
  ref_type: string | null;
  ref_id: string | null;
  balance_after: number;
  created_at: string;
}

export interface CreditGrantBucket {
  amount: number;
  remaining: number;
  reason: string;
  expires_at: string | null;
  created_at: string;
}

export interface NewlyGrantedCredits {
  offer: 'promo' | 'base';
  credits: number;
  expires_at: string | null;
}

export interface CreditOverview {
  balance: number;
  /** Signup credits this very request granted (automatic, once per workspace). Empty on every later load. */
  newly_granted?: NewlyGrantedCredits[];
  signup_claims: { promo_claimed: boolean; base_claimed: boolean };
  first_recharge_discount_available: boolean;
  usage_mtd: Array<{ reason: string; spent: number }>;
  grants: CreditGrantBucket[];
  recent_ledger: CreditLedgerEntry[];
}

export interface CreditsBillingInfo {
  packs_usd: number[];
  min_purchase_usd: number;
  first_recharge_discount: number;
  configured: boolean;
}

export async function getCredits(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; credits: CreditOverview; billing: CreditsBillingInfo }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/credits?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch credits');
  return data;
}

export async function claimSignupCredits(params: {
  firebase_uid: string;
  workspace_id: string;
  offer: 'promo' | 'base';
}): Promise<{ success: boolean; claimed: number; expires_at: string; balance: number }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/credits/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, offer: params.offer }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to claim credits');
  return data;
}

export async function createCreditCheckout(params: {
  firebase_uid: string;
  workspace_id: string;
  amount_usd: number;
}): Promise<{ success: boolean; checkout_url: string; charge_usd: number; credits: number; discount_applied: boolean }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/credits/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, amount_usd: params.amount_usd }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create checkout');
  return data;
}

export interface CreditInvoice {
  ledger_id: number;
  payment_id: string | null;
  credits: number;
  balance_after: number;
  workspace_date: string;
  status: string | null;
  amount_minor: number | null;
  currency: string | null;
  settlement_amount: number | null;
  settlement_currency: string | null;
  settlement_tax: number | null;
  paid_at: string | null;
  invoice_url: string | null;
}

export async function getInvoices(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; invoices: CreditInvoice[]; dodo_configured: boolean }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/credits/invoices?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch invoices');
  return data;
}

export interface PaymentReceipt {
  payment_id: string;
  payment_status: string | null;
  paid_at: string | null;
  amount_minor: number | null;
  currency: string | null;
  settlement_amount: number | null;
  settlement_currency: string | null;
  settlement_tax: number | null;
  card: { network: string; last_four: number; holder: string | null } | null;
  credits: number | null;
  balance_after: number | null;
  credits_expires_at: string | null;
  invoice_url: string | null;
}

export async function getPaymentReceipt(params: {
  firebase_uid: string;
  workspace_id: string;
  payment_id?: string;
}): Promise<{ success: boolean; receipt: PaymentReceipt | null; payment_status?: string; workspace: { workspace_id: string; name: string; logo_url: string | null } }> {
  const qs = new URLSearchParams({ firebase_uid: params.firebase_uid });
  if (params.payment_id) qs.set('payment_id', params.payment_id);
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/credits/receipt?${qs.toString()}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch payment receipt');
  return data;
}

// ─── SLA Tracking ─────────────────────────────────────────

export interface SlaPolicy {
  id: number;
  name: string;
  category: string | null;
  priorities: Record<string, { fr: number; res: number }>;
  is_default: boolean;
  pause_on_waiting: boolean;
  is_active: boolean;
}

export interface BusinessHours {
  timezone: string;
  day_ranges: Record<string, [string, string] | null>;
  holidays: string[];
}

export async function getSlaPolicies(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; policies: SlaPolicy[]; business_hours: BusinessHours | null }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/sla/policies?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load SLA policies');
  return data;
}

export async function saveSlaPolicy(params: {
  firebase_uid: string;
  workspace_id: string;
  name: string;
  category?: string | null;
  priorities: Record<string, { fr: number; res: number }>;
  is_default?: boolean;
  pause_on_waiting?: boolean;
  is_active?: boolean;
  apply_to_open?: boolean;
  policy_id?: number; // present = update
}): Promise<{ success: boolean; policy: SlaPolicy; open_tickets_recomputed: number }> {
  const body: Record<string, unknown> = {
    firebase_uid: params.firebase_uid,
    name: params.name,
    category: params.category ?? null,
    priorities: params.priorities,
    is_default: params.is_default ?? false,
    pause_on_waiting: params.pause_on_waiting ?? true,
    is_active: params.is_active ?? true,
    apply_to_open: params.apply_to_open ?? false,
  };
  const res = await apiFetch(
    params.policy_id
      ? `/workspace/${params.workspace_id}/sla/policies/${params.policy_id}`
      : `/workspace/${params.workspace_id}/sla/policies`,
    { method: params.policy_id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save SLA policy');
  return data;
}

export async function deleteSlaPolicy(params: {
  firebase_uid: string;
  workspace_id: string;
  policy_id: number;
}): Promise<{ success: boolean; deleted: number }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/sla/policies/${params.policy_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
    { method: 'DELETE' },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete SLA policy');
  return data;
}

export async function saveBusinessHours(params: {
  firebase_uid: string;
  workspace_id: string;
  timezone: string;
  day_ranges: Record<string, [string, string] | null>;
  holidays: string[];
}): Promise<{ success: boolean; business_hours: BusinessHours }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/sla/business-hours`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_uid: params.firebase_uid,
      timezone: params.timezone,
      day_ranges: params.day_ranges,
      holidays: params.holidays,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save business hours');
  return data;
}

// ─── Categories (managed entity) ─────────────────────────

export interface WorkspaceCategory {
  id: number;
  name: string;
  is_system: boolean;
  created_by: string;
  ticket_count: number;
  created_at?: string;
}

export async function getCategories(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; categories: WorkspaceCategory[]; ai_auto_category: boolean; viewer_role: string }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/categories?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load categories');
  return data;
}

export async function createCategory(params: {
  firebase_uid: string;
  workspace_id: string;
  name: string;
}): Promise<{ success: boolean; category: WorkspaceCategory }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/categories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, name: params.name }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create category');
  return data;
}

export async function renameCategory(params: {
  firebase_uid: string;
  workspace_id: string;
  category_id: number;
  name: string;
  merge?: boolean;
}): Promise<{ success: boolean; category: { id: number; name: string }; renamed_tickets: number }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/categories/${params.category_id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, name: params.name, merge: params.merge ?? false }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to rename category');
  return data;
}

export async function deleteCategory(params: {
  firebase_uid: string;
  workspace_id: string;
  category_id: number;
}): Promise<{ success: boolean; deleted: number }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/categories/${params.category_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
    { method: 'DELETE' },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete category');
  return data;
}

// ─── Magic Helpdesk (AI website scan → drafts) ──────────────

export interface KbMagicDraftedArticle {
  id: number;
  slug: string;
  title: string;
}

export interface KbMagicJob {
  id: number;
  status: 'queued' | 'running' | 'done' | 'failed';
  phase: string | null;
  pages_found: number;
  drafted: KbMagicDraftedArticle[];
  error: string | null;
  url: string;
  created_at: string;
}

export async function startKbMagicDraft(params: {
  firebase_uid: string;
  workspace_id: string;
  url?: string;
}): Promise<{ job: KbMagicJob; charged: boolean; balance: number }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/magic-draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, url: params.url }),
  });
  return kbJson(res, 'Failed to start Magic Helpdesk');
}

export async function getKbMagicJob(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ job: KbMagicJob | null }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/kb/magic-draft/latest?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  return kbJson(res, 'Failed to load Magic Helpdesk status');
}

// ─── Workspace deletion ────────────────────────────────────

export async function deleteWorkspaceCascade(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; purged: boolean; r2_deleted: number }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/kb/cascade-delete?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
    { method: 'POST' },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete workspace');
  return data;
}

export async function saveCategorySettings(params: {
  firebase_uid: string;
  workspace_id: string;
  ai_auto_category: boolean;
}): Promise<{ success: boolean; ai_auto_category: boolean }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/categories/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, ai_auto_category: params.ai_auto_category }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save category settings');
  return data;
}

// ─── Automation Rules ─────────────────────────────────────

export interface AutomationRule {
  id: number;
  name: string;
  is_active: boolean;
  needs_attention: boolean;
  priority: number;
  stop_on_match: boolean;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  conditions: Array<{ field: string; operator: string; value: string | string[] }>;
  actions: Array<Record<string, unknown>>;
  run_count: number;
  last_run_at: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AutomationRun {
  id: number;
  rule_id: number;
  ticket_id: number | null;
  ticket_number?: number;
  trigger_event: string;
  status: string;
  actions_taken: Array<{ action: string; result: string; error?: string }>;
  error: string | null;
  created_at: string;
  rule_name?: string;
}

export async function getAutomations(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ success: boolean; automations: AutomationRule[]; recent_runs: AutomationRun[]; workspace_enabled: boolean; viewer_role: string; beta: boolean }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/automations?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load automations');
  return data;
}

export async function saveAutomation(params: {
  firebase_uid: string;
  workspace_id: string;
  rule: Record<string, unknown>;
  rule_id?: number;
}): Promise<{ success: boolean; automation?: AutomationRule }> {
  const res = await apiFetch(
    params.rule_id
      ? `/workspace/${params.workspace_id}/automations/${params.rule_id}`
      : `/workspace/${params.workspace_id}/automations`,
    {
      method: params.rule_id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firebase_uid: params.firebase_uid, ...params.rule }),
    },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save rule');
  return data;
}

export async function deleteAutomation(params: {
  firebase_uid: string;
  workspace_id: string;
  rule_id: number;
}): Promise<{ success: boolean }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/automations/${params.rule_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
    { method: 'DELETE' },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete rule');
  return data;
}

export async function testDraftAutomation(params: {
  firebase_uid: string;
  workspace_id: string;
  rule: Record<string, unknown>;
}): Promise<{ success: boolean; dry_run: { matched: number; tickets: unknown[]; would_actions: string[] } }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/automations/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, ...params.rule }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Dry run failed');
  return data;
}

export async function testAutomation(params: {
  firebase_uid: string;
  workspace_id: string;
  rule_id: number;
}): Promise<{ success: boolean; dry_run: { matched: number; would_actions: string[] } }> {
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/automations/${params.rule_id}/test?firebase_uid=${encodeURIComponent(params.firebase_uid)}`,
    { method: 'POST' },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Test failed');
  return data;
}

export async function reorderAutomations(params: {
  firebase_uid: string;
  workspace_id: string;
  rule_ids: number[];
}): Promise<{ success: boolean }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/automations-order`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, rule_ids: params.rule_ids }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to reorder');
  return data;
}

export async function getAutomationRuns(params: {
  firebase_uid: string;
  workspace_id: string;
  rule_id: number;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; runs: AutomationRun[]; pagination: { total: number; page: number; limit: number } }> {
  const qs = new URLSearchParams({ firebase_uid: params.firebase_uid });
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const res = await apiFetch(
    `/workspace/${params.workspace_id}/automations/${params.rule_id}/runs?${qs.toString()}`,
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load runs');
  return data;
}

export async function saveAutomationSettings(params: {
  firebase_uid: string;
  workspace_id: string;
  automation_enabled: boolean;
}): Promise<{ success: boolean }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/automations/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid, automation_enabled: params.automation_enabled }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save settings');
  return data;
}

// ─── Helpdesk (blog-style KB) ──────────────────────────────────────────────

export interface KbTipTapDoc {
  type: string;
  attrs?: Record<string, unknown>;
  content?: KbTipTapDoc[];
  text?: string;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
}

export interface KbArticle {
  id: number;
  title: string;
  slug: string;
  summary: string;
  cover_key: string | null;
  meta_title: string | null;
  meta_description: string | null;
  content: KbTipTapDoc;
  status: 'draft' | 'published';
  visibility: 'public' | 'unlisted';
  is_seo: boolean;
  author: { id: number; name: string | null };
  published_at: string | null;
  first_publish_charged: boolean;
  publish_cost: number;
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  has_share_link: boolean;
  url_path: string | null;
}

export interface KbArticleDraftInput {
  title?: string;
  slug?: string;
  summary?: string;
  cover_key?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  content?: KbTipTapDoc;
  visibility?: 'public' | 'unlisted';
  is_seo?: boolean;
}

export interface KbRevision {
  id: number;
  title: string;
  summary: string;
  edited_by: number;
  editor_name: string | null;
  created_at: string;
}

export interface KbSettings {
  workspace_id: number;
  is_public: boolean;
  title: string | null;
  intro: string | null;
  meta_title: string | null;
  meta_description: string | null;
  meta_image_key: string | null;
  accent_color: string | null;
  light_fg: string | null;
  light_bg: string | null;
  dark_fg: string | null;
  dark_bg: string | null;
  title_font: string | null;
  body_font: string | null;
  subs_enabled: boolean;
}

export interface KbSlug {
  id: number;
  slug: string;
  is_primary: number;
  claimed_at: string;
}

export interface KbAnalytics {
  range_days: number | 'all';
  totals: { pageviews: number; visitors: number; subscribers: number };
  daily: { day: string; pageviews: number; visitors: number }[];
  referrers: { referrer_host: string; n: number }[];
  top_articles: { slug: string; title: string; view_count: number; helpful_count: number; not_helpful_count: number }[];
  searches: { query: string; result_count: number; n: number; last_at: string }[];
}

export interface KbSubscriber {
  id: number;
  email: string;
  created_at: string;
}

export interface KbShareLink {
  token: string;
  path: string;
  url: string;
  expires_at: string;
}

async function kbJson<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function listKbArticles(params: {
  firebase_uid: string;
  workspace_id: string;
  status?: 'draft' | 'published' | 'trash';
  q?: string;
  page?: number;
  limit?: number;
}): Promise<{ articles: KbArticle[]; pagination: { total: number; page: number; limit: number; total_pages: number }; helpcenter: { ident: string; path: string } }> {
  const qs = new URLSearchParams({ firebase_uid: params.firebase_uid });
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles?${qs.toString()}`);
  return kbJson(res, 'Failed to load articles');
}

export interface KbSuggestedArticle {
  id: number;
  slug: string;
  title: string;
  summary: string;
  unlisted: boolean;
  url: string;
}

/** Published helpdesk articles best matching a ticket (or free text). Free —
 *  no credit charge. `has_published: false` means hide the UI entirely. */
export async function suggestKbArticles(params: {
  firebase_uid: string;
  workspace_id: string;
  ticket_id?: number | string;
  q?: string;
}): Promise<{ has_published: boolean; articles: KbSuggestedArticle[]; helpdesk: { ident: string; links_public: boolean } }> {
  const qs = new URLSearchParams({ firebase_uid: params.firebase_uid });
  if (params.ticket_id !== undefined) qs.set('ticket_id', String(params.ticket_id));
  if (params.q) qs.set('q', params.q);
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/suggest?${qs.toString()}`);
  return kbJson(res, 'Failed to load suggested articles');
}

export async function getKbArticle(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
}): Promise<{ article: KbArticle }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  return kbJson(res, 'Failed to load article');
}

export async function createKbArticle(params: {
  firebase_uid: string;
  workspace_id: string;
} & KbArticleDraftInput): Promise<{ article: KbArticle }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return kbJson(res, 'Failed to create article');
}

export async function updateKbArticle(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
  autosave?: boolean;
  share?: boolean;
} & KbArticleDraftInput): Promise<{ article: KbArticle; share?: KbShareLink }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return kbJson(res, 'Failed to save article');
}

export async function deleteKbArticle(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
}): Promise<{ success: boolean }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`, {
    method: 'DELETE',
  });
  return kbJson(res, 'Failed to delete article');
}

export async function restoreKbArticle(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
}): Promise<{ article: KbArticle }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid }),
  });
  return kbJson(res, 'Failed to restore article');
}

export async function publishKbArticle(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
}): Promise<{ article: KbArticle; charged: boolean; balance: number | null }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid }),
  });
  return kbJson(res, 'Failed to publish article');
}

export async function unpublishKbArticle(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
}): Promise<{ article: KbArticle }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}/unpublish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid }),
  });
  return kbJson(res, 'Failed to unpublish article');
}

export async function shareKbArticle(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
  rotate?: boolean;
}): Promise<{ share: KbShareLink }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}/share${params.rotate ? '/rotate' : ''}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid }),
  });
  return kbJson(res, 'Failed to create draft link');
}

export async function listKbRevisions(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
}): Promise<{ revisions: KbRevision[] }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}/revisions?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  return kbJson(res, 'Failed to load revisions');
}

export async function getKbRevision(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
  rid: number;
}): Promise<{ revision: { id: number; title: string; summary: string; content: KbTipTapDoc; created_at: string } }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}/revisions/${params.rid}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  return kbJson(res, 'Failed to load revision');
}

export async function restoreKbRevision(params: {
  firebase_uid: string;
  workspace_id: string;
  article_id: number | string;
  rid: number;
}): Promise<{ article: KbArticle }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/articles/${params.article_id}/revisions/${params.rid}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid }),
  });
  return kbJson(res, 'Failed to restore revision');
}

export interface KbSettingsBundle {
  settings: KbSettings;
  slugs: KbSlug[];
  slug_claims_remaining: number;
  max_slug_claims: number;
  default_ident: string;
}

export async function getKbSettings(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<KbSettingsBundle> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/settings?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  return kbJson(res, 'Failed to load helpdesk settings');
}

export type KbSettingsPatch = Partial<Omit<KbSettings, 'workspace_id'>>;

export async function saveKbSettings(params: {
  firebase_uid: string;
  workspace_id: string;
} & KbSettingsPatch): Promise<{ settings: KbSettings }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return kbJson(res, 'Failed to save settings');
}

export async function claimKbSlug(params: {
  firebase_uid: string;
  workspace_id: string;
  slug: string;
}): Promise<{ slug: string; remaining: number }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/slugs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return kbJson(res, 'Failed to claim link');
}

export async function setPrimaryKbSlug(params: {
  firebase_uid: string;
  workspace_id: string;
  slug_id: number;
}): Promise<{ primary: string }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/slugs/${params.slug_id}/primary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebase_uid: params.firebase_uid }),
  });
  return kbJson(res, 'Failed to set primary link');
}

export async function getKbAnalytics(params: {
  firebase_uid: string;
  workspace_id: string;
  days?: 7 | 30 | 90 | 'all';
}): Promise<{ analytics: KbAnalytics }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/analytics?firebase_uid=${encodeURIComponent(params.firebase_uid)}&days=${params.days ?? 30}`);
  return kbJson(res, 'Failed to load analytics');
}

export async function listKbSubscribers(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ subscribers: KbSubscriber[]; total: number }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/subscribers?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  return kbJson(res, 'Failed to load subscribers');
}

export async function deleteKbSubscriber(params: {
  firebase_uid: string;
  workspace_id: string;
  sub_id: number;
}): Promise<{ success: boolean }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/subscribers/${params.sub_id}?firebase_uid=${encodeURIComponent(params.firebase_uid)}`, {
    method: 'DELETE',
  });
  return kbJson(res, 'Failed to remove subscriber');
}

/** Downloads the subscriber CSV via an authenticated fetch (the endpoint needs
 *  a Bearer token, so a plain <a href> won't work). */
export async function exportKbSubscribersCsv(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<void> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/kb/subscribers.csv?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  if (!res.ok) throw new Error('Failed to export subscribers');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `help-center-subscribers.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function uploadKbAsset(params: {
  file: File;
  firebase_uid: string;
  workspace_id: string;
  article_id?: number | null;
}): Promise<{ key: string; url: string; attachment: { file_type: string } }> {
  return uploadAttachment({
    file: params.file,
    firebase_uid: params.firebase_uid,
    workspace_id: params.workspace_id,
    attachment_type: 'kb',
  }).then((r) => {
    const url = (r as unknown as { key?: string; url?: string });
    if (!url?.key || !url?.url) throw new Error('Upload failed');
    return { key: url.key!, url: url.url!, attachment: { file_type: r.attachment.file_type } };
  });
}


// ── Workspace branding (shared by helpdesk, customer portal and customer emails) ──

export interface WorkspaceBranding {
  logo_url: string | null;
  accent_color: string | null;
  light_fg: string | null;
  light_bg: string | null;
  dark_fg: string | null;
  dark_bg: string | null;
  title_font: string | null;
  body_font: string | null;
}

export type WorkspaceBrandingPatch = Partial<Omit<WorkspaceBranding, 'logo_url'>>;

export async function getWorkspaceBranding(params: {
  firebase_uid: string;
  workspace_id: string;
}): Promise<{ branding: WorkspaceBranding }> {
  const res = await apiFetch(`/workspace/${params.workspace_id}/branding?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load branding');
  return data;
}

export async function saveWorkspaceBranding(params: {
  firebase_uid: string;
  workspace_id: string;
} & WorkspaceBrandingPatch): Promise<{ branding: WorkspaceBranding }> {
  const { workspace_id, ...rest } = params;
  const res = await apiFetch(`/workspace/${workspace_id}/branding`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rest),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save branding');
  return data;
}


// ── Customer portal (team side) ──

export interface PortalSettings {
  portal_enabled: boolean;
  signin_mode: 'open' | 'invite';
  allow_ticket_create: boolean;
  show_agent_names: boolean;
  welcome_text: string | null;
  privacy_url: string | null;
  hourly_ticket_cap: number;
  customer_daily_cap: number;
  default_category: string | null;
  retention_months: number;
}

export interface PortalCustomer {
  id: number;
  email: string;
  display_name: string | null;
  status: 'invited' | 'active' | 'blocked';
  created_at: string;
  last_login_at: string | null;
  ticket_count: number;
}

async function portalAdminJson<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || fallback);
  return data as T;
}

export async function getPortalSettings(params: { firebase_uid: string; workspace_id: string }) {
  const res = await apiFetch(`/workspace/${params.workspace_id}/portal/settings?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  return portalAdminJson<{ settings: PortalSettings; portal_url: string; counts: { customers: number; active: number } }>(res, 'Failed to load portal settings');
}

export async function savePortalSettings(params: { firebase_uid: string; workspace_id: string } & Partial<PortalSettings>) {
  // `workspace_id` is the public id for the URL only; never let a settings field of the same name
  // (the server used to echo its internal numeric id) overwrite it.
  const { workspace_id, ...rest } = params;
  delete (rest as Record<string, unknown>).workspace_id;
  const res = await apiFetch(`/workspace/${workspace_id}/portal/settings`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rest),
  });
  return portalAdminJson<{ settings: PortalSettings }>(res, 'Failed to save portal settings');
}

export async function listPortalCustomers(params: { firebase_uid: string; workspace_id: string; search?: string; page?: number }) {
  const q = new URLSearchParams({ firebase_uid: params.firebase_uid });
  if (params.search) q.set('search', params.search);
  if (params.page) q.set('page', String(params.page));
  const res = await apiFetch(`/workspace/${params.workspace_id}/portal/customers?${q.toString()}`);
  return portalAdminJson<{ customers: PortalCustomer[]; total: number; page: number; has_more: boolean }>(res, 'Failed to load customers');
}

export async function invitePortalCustomer(params: { firebase_uid: string; workspace_id: string; email: string; display_name?: string }) {
  const { workspace_id, ...rest } = params;
  const res = await apiFetch(`/workspace/${workspace_id}/portal/customers/invite`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rest),
  });
  return portalAdminJson<{ customer: { id: number; email: string; status: string } }>(res, 'Failed to invite customer');
}

export async function updatePortalCustomer(params: { firebase_uid: string; workspace_id: string; customer_id: number; status?: 'blocked' | 'active'; display_name?: string }) {
  const { workspace_id, customer_id, ...rest } = params;
  const res = await apiFetch(`/workspace/${workspace_id}/portal/customers/${customer_id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rest),
  });
  return portalAdminJson<{ customer: PortalCustomer }>(res, 'Failed to update customer');
}

export interface DataRequestRow {
  id: number; customer_label: string | null; type: 'export' | 'erase'; requested_by: 'customer' | 'owner';
  reason: string | null; status: string; created_at: string; completed_at: string | null; actor: string | null;
}

export async function exportPortalCustomer(params: { firebase_uid: string; workspace_id: string; customer_id: number; reason: string }) {
  const { workspace_id, customer_id, ...rest } = params;
  const res = await apiFetch(`/workspace/${workspace_id}/portal/customers/${customer_id}/export`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rest),
  });
  return portalAdminJson<{ download: { json_url: string; csv_url: string; expires_at: string } }>(res, 'Failed to export data');
}

export async function erasePortalCustomer(params: { firebase_uid: string; workspace_id: string; customer_id: number; reason: string; confirm: string }) {
  const { workspace_id, customer_id, ...rest } = params;
  const res = await apiFetch(`/workspace/${workspace_id}/portal/customers/${customer_id}/erase`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rest),
  });
  return portalAdminJson<{ summary: { tickets: number; comments: number; files: number } }>(res, 'Failed to erase data');
}

export async function listDataRequests(params: { firebase_uid: string; workspace_id: string }) {
  const res = await apiFetch(`/workspace/${params.workspace_id}/portal/data-requests?firebase_uid=${encodeURIComponent(params.firebase_uid)}`);
  return portalAdminJson<{ requests: DataRequestRow[] }>(res, 'Failed to load data requests');
}

// ── Branding auto-fetch + tickets for portal customers ───────────────────────

export interface BrandingSuggestion {
  accent_color: string | null;
  light_fg: string | null; light_bg: string | null;
  dark_fg: string | null; dark_bg: string | null;
  title_font: string | null; body_font: string | null;
}

export async function suggestWorkspaceBranding(params: { firebase_uid: string; workspace_id: string; url?: string; logo_only?: boolean }) {
  const { workspace_id, ...rest } = params;
  const res = await apiFetch(`/workspace/${workspace_id}/branding/suggest`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rest),
  });
  const data = await res.json();
  if (!res.ok) { const e = new Error(data.error || 'Could not read that website') as Error & { code?: string }; e.code = data.code; throw e; }
  return data as {
    suggestion: BrandingSuggestion | null;
    logo: { data_url: string; source_url: string; kind: string } | null;
    source: 'ai' | 'heuristic';
    site: { url: string; title: string };
    remaining_today: number;
  };
}

export async function createTicketForPortalCustomer(params: {
  firebase_uid: string; workspace_id: string; customer_id: number;
  title: string; description: string; priority?: string; category?: string;
}) {
  const { workspace_id, customer_id, ...rest } = params;
  const res = await apiFetch(`/workspace/${workspace_id}/portal/customers/${customer_id}/tickets`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rest),
  });
  return portalAdminJson<{ ticket: { id: number; ticket_number: number } }>(res, 'Failed to create ticket');
}


// ── Kora AI (reply drafting + resolution agent) ──

export type AiAgentMode = 'off' | 'approval' | 'auto';
export type AiSourceKey = 'exceptions' | 'knowledge' | 'articles';

export interface AiAccess {
  available: boolean;
  can_manage: boolean;
  can_view: boolean;
  draft_allowed: boolean;
  paused: boolean;
  draft_enabled: boolean;
  agent_mode: AiAgentMode;
  prices: { draft: number; resolution: number };
}

export interface AiSettings {
  draft_enabled: boolean;
  draft_roles: string[];
  tone: 'friendly' | 'formal' | 'concise';
  signature: string;
  language_hint: string;
  agent_mode: AiAgentMode;
  agent_channels: ('portal' | 'dashboard')[];
  confidence_threshold: number;
  excluded_categories: string[];
  handoff_message: string;
  daily_credit_cap: number;
  source_order: AiSourceKey[];
  learn_from_replies: boolean;
  paused: boolean;
}

export interface AiSettingsResponse {
  settings: AiSettings;
  options: { draftable_roles: string[]; thresholds: Record<string, number>; categories: string[]; prices: { draft: number; resolution: number } };
  counts: { exceptions: number; exceptions_enabled: number; knowledge: number; articles: number };
}

export interface AiKnowledgeItem {
  id: number;
  kind: 'file' | 'entry';
  title: string;
  file_name: string | null;
  file_size: number;
  status: 'processing' | 'ready' | 'failed';
  error: string | null;
  enabled: boolean;
  chars?: number;
  created_at: string;
  updated_at: string;
}

export interface AiKnowledgeUsage { bytes: number; count: number; limit_bytes: number; limit_items: number }

export interface AiException {
  id: number;
  title: string;
  condition_text: string;
  keywords: string[];
  action: 'answer_with' | 'always_escalate' | 'never_answer';
  instruction: string;
  categories: string[];
  enabled: boolean;
  position: number;
}

export interface AiCitation { type: 'exception' | 'knowledge' | 'article'; id: number; title: string; snippet?: string; slug?: string }

export interface AiRun {
  id: number;
  kind: 'draft' | 'agent';
  status: 'ok' | 'no_answer' | 'escalated' | 'held' | 'sent' | 'resolved' | 'rejected' | 'failed' | 'blocked' | 'running';
  mode: string | null;
  created_at: string;
  ticket: { id: number; number: number; title: string };
  customer_message: string;
  answer: string;
  citations: AiCitation[];
  confidence: number | null;
  reason: string | null;
  credits_charged: number;
  ai_state: string | null;
  decided_at: string | null;
}

export interface AiMetrics {
  drafts: { count: number; credits: number; used_pct: number | null; as_is: number; not_helpful: number };
  agent: { runs: number; resolved: number; escalated: number; answered: number; held: number; credits: number; resolution_rate: number | null };
  credits_used: number;
  series: { day: string; resolved: number; escalated: number; drafts: number }[];
}

export interface AiDraftResult {
  run_id: number;
  status: 'ok' | 'no_answer';
  draft: string | null;
  citations: AiCitation[];
  confidence: number;
  reason: string | null;
  credits_charged: number;
  credits_balance: number;
}

type AiParams = { firebase_uid: string; workspace_id: string };

async function aiJson<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.error || fallback) as Error & { code?: string };
    e.code = data.code;
    throw e;
  }
  return data as T;
}

const aiGet = (p: AiParams, path: string, extra = '') =>
  apiFetch(`/workspace/${p.workspace_id}/ai/${path}?firebase_uid=${encodeURIComponent(p.firebase_uid)}${extra}`);
const aiSend = (p: AiParams, method: string, path: string, body: Record<string, unknown> = {}) =>
  apiFetch(`/workspace/${p.workspace_id}/ai/${path}`, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firebase_uid: p.firebase_uid, ...body }) });

export const getAiAccess = async (p: AiParams) => aiJson<AiAccess & { success: boolean }>(await aiGet(p, 'access'), 'Failed to load Kora AI');
export const getAiSettings = async (p: AiParams) => aiJson<AiSettingsResponse>(await aiGet(p, 'settings'), 'Failed to load Kora AI settings');
export const saveAiSettings = async (p: AiParams, patch: Partial<AiSettings>) => aiJson<{ settings: AiSettings }>(await aiSend(p, 'PUT', 'settings', patch), 'Failed to save Kora AI settings');

export const listAiKnowledge = async (p: AiParams) => aiJson<{ items: AiKnowledgeItem[]; usage: AiKnowledgeUsage }>(await aiGet(p, 'knowledge'), 'Failed to load knowledge');
export const createAiEntry = async (p: AiParams, body: { title: string; body: string }) => aiJson<{ item: AiKnowledgeItem }>(await aiSend(p, 'POST', 'knowledge/entry', body), 'Failed to save the entry');
export const updateAiKnowledge = async (p: AiParams, id: number, patch: { enabled?: boolean; title?: string; body?: string }) => aiJson<{ item: AiKnowledgeItem }>(await aiSend(p, 'PATCH', `knowledge/${id}`, patch), 'Failed to update');
export const deleteAiKnowledge = async (p: AiParams, id: number) => aiJson<{ success: boolean }>(await apiFetch(`/workspace/${p.workspace_id}/ai/knowledge/${id}?firebase_uid=${encodeURIComponent(p.firebase_uid)}`, { method: 'DELETE' }), 'Failed to delete');
export async function uploadAiKnowledge(p: AiParams, files: File[]) {
  const form = new FormData();
  files.forEach((f) => form.append('file', f));
  const res = await apiFetch(`/workspace/${p.workspace_id}/ai/knowledge/upload?firebase_uid=${encodeURIComponent(p.firebase_uid)}`, { method: 'POST', body: form });
  return aiJson<{ items: AiKnowledgeItem[]; results: { ok: boolean; file: string; error?: string; item?: AiKnowledgeItem }[] }>(res, 'Upload failed');
}

export const listAiExceptions = async (p: AiParams) => aiJson<{ exceptions: AiException[] }>(await aiGet(p, 'exceptions'), 'Failed to load exceptions');
export const createAiException = async (p: AiParams, body: Partial<AiException>) => aiJson<{ exception: AiException }>(await aiSend(p, 'POST', 'exceptions', body), 'Failed to save the exception');
export const updateAiException = async (p: AiParams, id: number, body: Partial<AiException>) => aiJson<{ exception: AiException }>(await aiSend(p, 'PUT', `exceptions/${id}`, body), 'Failed to update the exception');
export const deleteAiException = async (p: AiParams, id: number) => aiJson<{ success: boolean }>(await apiFetch(`/workspace/${p.workspace_id}/ai/exceptions/${id}?firebase_uid=${encodeURIComponent(p.firebase_uid)}`, { method: 'DELETE' }), 'Failed to delete the exception');
export const reorderAiExceptions = async (p: AiParams, ids: number[]) => aiJson<{ exceptions: AiException[] }>(await aiSend(p, 'PUT', 'exceptions-order', { ids }), 'Failed to reorder');
export const testAiMessage = async (p: AiParams, message: string, category?: string) =>
  aiJson<{ outcome: 'answer' | 'no_answer' | 'escalate' | 'failed'; answer: string | null; citations: AiCitation[]; confidence: number; reason: string; exception_ids: number[] }>(
    await aiSend(p, 'POST', 'exceptions/test', { message, category }), 'The test failed');

export const listAiRuns = async (p: AiParams, q: { status?: string; kind?: string; page?: number } = {}) => {
  const extra = `${q.status ? `&status=${q.status}` : ''}${q.kind ? `&kind=${q.kind}` : ''}${q.page ? `&page=${q.page}` : ''}`;
  return aiJson<{ runs: AiRun[]; total: number; page: number; held: number }>(await aiGet(p, 'runs', extra), 'Failed to load activity');
};
export const approveAiRun = async (p: AiParams, id: number, answer?: string) => aiJson<{ success: boolean }>(await aiSend(p, 'POST', `runs/${id}/approve`, answer ? { answer } : {}), 'Failed to send the answer');
export const rejectAiRun = async (p: AiParams, id: number) => aiJson<{ success: boolean }>(await aiSend(p, 'POST', `runs/${id}/reject`), 'Failed to reject the answer');
export const getAiMetrics = async (p: AiParams) => aiJson<AiMetrics>(await aiGet(p, 'metrics'), 'Failed to load metrics');

export async function draftAiReply(p: AiParams, ticketId: number, regenerateOf?: number) {
  const res = await apiFetch(`/workspace/${p.workspace_id}/tickets/${ticketId}/ai/draft`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firebase_uid: p.firebase_uid, ...(regenerateOf ? { regenerate_of: regenerateOf } : {}) }),
  });
  return aiJson<AiDraftResult>(res, 'Kora AI could not draft a reply');
}
export const sendAiDraftFeedback = async (p: AiParams, runId: number, signal: 'sent_as_is' | 'edited' | 'discarded' | 'thumbs_up' | 'thumbs_down', editRatio?: number) =>
  aiJson<{ success: boolean }>(await aiSend(p, 'POST', `runs/${runId}/feedback`, { signal, ...(editRatio !== undefined ? { edit_ratio: editRatio } : {}) }), 'Failed to send feedback');
